import { useAccommodationState } from '@/features/product/hooks/useAccommodationState';
import ProductListItemContentErrorBoundary from '@/features/product/ui/ErrorBoundary';
import ProductTotal from '@/features/product/ui/Total';
import { cn } from '@/lib/utils';

import AccommodationCheckInOutDate from './CheckInOutDate';
import AccommodationGuestQuantity from './GuestQuantity';
import ProductIncludedExcluded from './IncludedExcluded';
import AccommodationOptions from './Options';

interface IProps {
  idx: string;
  shouldShow: boolean;
}
export default function AccommodationListItemContent({ idx, shouldShow }: IProps) {
  return (
    <ProductListItemContentErrorBoundary>
      <div
        className={cn(
          'overflow-hidden transition-all duration-500 ease-in-out',
          shouldShow ? 'mb-5 max-h-[9999px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div
          className={cn(
            'transform rounded-3xl border-2 border-[#E9ECEF] bg-white transition-all duration-500 ease-in-out sm:rounded-[28px]',
            shouldShow ? 'translate-y-0 scale-100' : '-translate-y-4 scale-95',
          )}
        >
          <div
            className={cn(
              'p-5 transition-all delay-100 duration-700 sm:p-8',
              shouldShow ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
            )}
            data-testid='content-container'
          >
            <ContentBoundary idx={idx} shouldShow={shouldShow} />
          </div>
        </div>
      </div>
    </ProductListItemContentErrorBoundary>
  );
}

function ContentBoundary({
  idx,
  shouldShow,
}: Omit<IProps, 'selectedIdx' | 'isExpanded'> & { shouldShow: boolean }) {
  const {
    t,
    roomData,
    roomStatusList,
    addPeopleQuantity,
    selectedOption,
    currentMonth,
    selectedDates,
    selectedPeriodAddPeoplePrice,
    selectedPeriodRoomPriceList,

    // price
    totalRoomPrice,
    calculatedAddPeoplePrice,
    totalExtraGuestPrice,
    totalOptionPrice,
    totalAmount,

    updateQuantity,
    handleOptionSelection,
    addRoom,
    handleDateClick,
    handleChangeMonth,
  } = useAccommodationState(idx, shouldShow);

  return (
    <>
      <ProductIncludedExcluded included={roomData?.included} excluded={roomData?.excluded} />

      <AccommodationCheckInOutDate
        currentMonth={currentMonth}
        selectedDates={selectedDates}
        roomStatusList={roomStatusList}
        checkInTime={roomData?.checkInTime}
        checkOutTime={roomData?.checkOutTime}
        handleDateClick={handleDateClick}
        handleChangeMonth={handleChangeMonth}
      />

      <AccommodationOptions
        roomOptionTypeList={roomData?.roomOptionTypeList}
        selectedOption={selectedOption}
        handleOptionSelection={handleOptionSelection}
      />

      <AccommodationGuestQuantity
        selectedDates={selectedDates}
        selectedPeriodAddPeoplePrice={selectedPeriodAddPeoplePrice}
        selectedPeriodRoomPriceList={selectedPeriodRoomPriceList}
        calculatedAddPeoplePrice={calculatedAddPeoplePrice}
        addPeopleQuantity={addPeopleQuantity}
        updateQuantity={updateQuantity}
        standardPeople={roomData?.standardPeople}
        addPeople={roomData?.addPeople}
      />

      <ProductTotal
        totalList={[
          {
            label: t('roomPrice'),
            price: totalRoomPrice,
          },
          {
            label: t('extraGuestPrice'),
            price: totalExtraGuestPrice,
          },
          {
            label: t('optionPrice'),
            price: totalOptionPrice,
          },
        ]}
        totalAmount={totalAmount}
        addItemLabel={t('addRoom')}
        addItem={addRoom}
      />
    </>
  );
}
